import { useState } from 'react';

import {
  OCR_CAPABILITY_OPTIONS,
  OCR_MODEL_TIER_OPTIONS,
  type OcrCapability,
  type OcrModelTier,
  type TenantOcrProviderResponse,
} from '../api';
import { useTenantOcrProviders } from '../hooks/useTenantOcrProviders';
import { OcrModelTierSelect } from './OcrModelTierSelect';

/** Sentinel value for the Document Intelligence default in the <select> (maps to ocrProviderId=null). */
const DI_VALUE = '__di__';

interface OcrSourceSelectProps {
  /** Selected platform OCR provider id, or null for the Azure Document Intelligence default. */
  ocrProviderId: string | null;
  /** OCR model tier — used only on the DI default path. */
  ocrModelTier: OcrModelTier;
  /** Capability flag names — used only on the provider path. */
  ocrCapabilities: string[];
  /** Switches the OCR source. `providerId` null = DI default. `defaults` are the provider's capabilities. */
  onChange: (next: { providerId: string | null; capabilities: string[] }) => void;
  onTierChange: (tier: OcrModelTier) => void;
  onCapabilitiesChange: (capabilities: string[]) => void;
  disabled?: boolean;
  /**
   * Whether this tenant may choose the OCR source. When false (the platform admin has not granted the
   * privilege) the control renders a read-only summary of the current source instead of the picker.
   */
  canEdit?: boolean;
}

/**
 * Picks the OCR engine for a document type:
 *  - «Azure Document Intelligence (por licencia)» → DI default (ocrProviderId=null), with the existing
 *    model-tier selector.
 *  - one option per active tenant-available OCR provider (Mistral…) → that provider id, with capability
 *    checkboxes defaulting to the provider's configured capabilities.
 * It is the first of the two-step OCR → LLM flow; the LLM provider is chosen separately below.
 */
export function OcrSourceSelect({
  ocrProviderId,
  ocrModelTier,
  ocrCapabilities,
  onChange,
  onTierChange,
  onCapabilitiesChange,
  disabled = false,
  canEdit = true,
}: OcrSourceSelectProps): JSX.Element {
  const { data: providers, isLoading } = useTenantOcrProviders();
  const available: TenantOcrProviderResponse[] = Array.isArray(providers) ? providers : [];

  const selectedProvider =
    ocrProviderId !== null ? available.find((p) => p.id === ocrProviderId) : undefined;
  // A persisted provider id that the tenant can no longer see (license change / deactivation): keep it
  // selectable so the saved value isn't silently dropped, but flag it.
  const selectionMissing = ocrProviderId !== null && selectedProvider === undefined && !isLoading;

  // "Automático" is the default, simplest state: no pinned provider and the inferred OCR tier. A type that
  // pins a provider or an explicit tier is already customised, so the advanced controls stay revealed (a
  // customised type can never collapse to the summary and silently hide its saved override).
  // NOTE: this hook must run before any early return (rules-of-hooks), hence above the read-only branch.
  const isCustomised = ocrProviderId !== null || ocrModelTier !== 'Auto';
  const [showAdvanced, setShowAdvanced] = useState(false);
  const expanded = showAdvanced || isCustomised;

  // Tenants without the platform-admin-granted privilege see a read-only summary of the current source —
  // never the picker. The server enforces the same gate, so this is purely UX.
  if (!canEdit) {
    const tierLabel =
      OCR_MODEL_TIER_OPTIONS.find((o) => o.value === ocrModelTier)?.label ?? ocrModelTier;
    const summary =
      selectedProvider !== undefined
        ? `${selectedProvider.displayName}${selectedProvider.modelId != null ? ` — ${selectedProvider.modelId}` : ''}`
        : ocrProviderId !== null
          ? `Proveedor (${ocrProviderId})`
          : ocrModelTier !== 'Auto'
            ? `Azure Document Intelligence · ${tierLabel}`
            : 'Automático';
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="label mb-1">Fuente OCR</p>
        <p className="text-sm font-medium text-slate-800">{summary}</p>
        <p className="mt-1 text-xs text-slate-500">
          La fuente OCR la gestiona tu administrador. Pídele que la cambie si lo necesitas.
        </p>
      </div>
    );
  }

  const handleSelect = (value: string): void => {
    if (value === DI_VALUE) {
      onChange({ providerId: null, capabilities: [] });
      return;
    }
    const provider = available.find((p) => p.id === value);
    onChange({ providerId: value, capabilities: provider?.capabilities ?? ['Text'] });
  };

  const toggleCapability = (cap: OcrCapability): void => {
    onCapabilitiesChange(
      ocrCapabilities.includes(cap)
        ? ocrCapabilities.filter((c) => c !== cap)
        : [...ocrCapabilities, cap],
    );
  };

  // Drop every override and collapse back to the recommended automatic behaviour.
  const resetToAutomatic = (): void => {
    onChange({ providerId: null, capabilities: [] });
    onTierChange('Auto');
    setShowAdvanced(false);
  };

  if (!expanded) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-sm font-medium text-slate-800">Automático (recomendado)</p>
        <p className="mt-1 text-xs text-slate-500">
          El sistema elige el motor OCR óptimo para cada documento: texto simple → Azure Document
          Intelligence (más barato); tablas o formularios → el motor avanzado que incluya tu plan, con
          vuelta automática a Document Intelligence si falla.
        </p>
        <button
          type="button"
          className="mt-2 text-xs font-medium text-primary hover:underline"
          onClick={() => setShowAdvanced(true)}
          disabled={disabled}
        >
          Configuración avanzada
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Configuración avanzada
        </span>
        <button
          type="button"
          className="text-xs font-medium text-primary hover:underline"
          onClick={resetToAutomatic}
          disabled={disabled}
        >
          Volver a Automático
        </button>
      </div>
      <div>
        <label className="label" htmlFor="ocr-source">
          Fuente OCR
        </label>
        <select
          id="ocr-source"
          className="input"
          value={ocrProviderId ?? DI_VALUE}
          disabled={disabled || isLoading}
          onChange={(e) => handleSelect(e.target.value)}
        >
          <option value={DI_VALUE}>Azure Document Intelligence (por licencia)</option>
          {available.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
              {p.modelId != null ? ` — ${p.modelId}` : ''}
            </option>
          ))}
          {selectionMissing ? (
            <option value={ocrProviderId ?? ''}>Proveedor no disponible ({ocrProviderId})</option>
          ) : null}
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Paso 1 de 2: elige el motor que extrae el texto del documento. El modelo de IA que lo convierte
          a JSON se elige más abajo.
        </p>
      </div>

      {ocrProviderId === null ? (
        <OcrModelTierSelect value={ocrModelTier} onChange={onTierChange} disabled={disabled} />
      ) : (
        <div>
          <p className="label mb-1">Capacidades a extraer</p>
          <div className="flex flex-wrap gap-4">
            {OCR_CAPABILITY_OPTIONS.map((c) => (
              <label key={c.value} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={ocrCapabilities.includes(c.value)}
                  onChange={() => toggleCapability(c.value)}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                {c.label}
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            En Mistral, los «Pares clave/valor» los deriva el LLM, no el OCR nativo.
          </p>
        </div>
      )}
    </div>
  );
}
